/**
 * Generated Questions Preview
 * Shows generated questions before saving, with refine option
 * Supports MCQ, Short Answer, and Case Study (grouped sub-questions) display
 */

import { useState, useMemo } from 'react';
import { Save, Trash2, Sparkles, Eye, EyeOff, FileText, CheckCircle, XCircle, ChevronDown, ChevronUp, Clock, MessageCircle, ArrowRight, Star } from 'lucide-react';
import { QuestionRefinementModal } from './QuestionRefinementModal';
import { questionsService } from '../services/questions.service';
import { formatAlternativeText } from '../utils/textFormatting';

interface GeneratedQuestionsPreviewProps {
  questions: any[];
  onSave: (questions: any[]) => void;
  onCancel: () => void;
}

/** A case study group: shared context + array of sub-questions */
interface CaseStudyGroup {
  kind: 'case_study';
  caseTitle: string;
  caseContext: string;
  caseData?: Record<string, any>;
  metadados?: Record<string, any>;
  subQuestions: any[];
}

/** An interactive question item */
interface InteractiveItem {
  kind: 'interactive';
  question: any;
}

/** A regular (non-case-study) question */
interface RegularItem {
  kind: 'regular';
  question: any;
}

type PreviewItem = CaseStudyGroup | InteractiveItem | RegularItem;

export function GeneratedQuestionsPreview({
  questions: initialQuestions,
  onSave,
  onCancel
}: GeneratedQuestionsPreviewProps) {
  const [questions, setQuestions] = useState(initialQuestions);
  const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
  const [showRefinementModal, setShowRefinementModal] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Group questions: case study sub-questions grouped by caseTitle, others kept flat
  const previewItems: PreviewItem[] = useMemo(() => {
    const items: PreviewItem[] = [];
    const caseGroups = new Map<string, CaseStudyGroup>();
    const processedTempIds = new Set<string>();

    // First pass: group case study questions
    for (const q of questions) {
      if ((q.type === 'case_study' || q.type === 'case_based') && q.caseTitle) {
        const key = q.caseTitle;
        if (!caseGroups.has(key)) {
          caseGroups.set(key, {
            kind: 'case_study',
            caseTitle: q.caseTitle,
            caseContext: q.caseContext || '',
            caseData: q.caseData,
            metadados: q.metadados,
            subQuestions: [],
          });
        }
        caseGroups.get(key)!.subQuestions.push(q);
        processedTempIds.add(q.tempId);
      }
    }

    // Second pass: build ordered list preserving original question order
    const usedCaseKeys = new Set<string>();
    for (const q of questions) {
      if (processedTempIds.has(q.tempId)) {
        const key = q.caseTitle;
        if (!usedCaseKeys.has(key)) {
          usedCaseKeys.add(key);
          const group = caseGroups.get(key)!;
          // Sort sub-questions by index
          group.subQuestions.sort((a: any, b: any) =>
            (a.subQuestionIndex || a.caseQuestionNumber || 0) -
            (b.subQuestionIndex || b.caseQuestionNumber || 0)
          );
          items.push(group);
        }
      } else if (q.type === 'interactive') {
        items.push({ kind: 'interactive', question: q });
      } else {
        items.push({ kind: 'regular', question: q });
      }
    }

    return items;
  }, [questions]);

  const toggleExpand = (key: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleRefineClick = (question: any) => {
    setSelectedQuestion(question);
    setShowRefinementModal(true);
  };

  const handleRefineSave = (refinedQuestion: any) => {
    setQuestions(prev =>
      prev.map(q =>
        q.tempId === selectedQuestion.tempId ? {
          ...q,
          ...refinedQuestion,
          tempId: q.tempId,
          exam: q.exam,
          exams: q.exams,
          course: q.course,
          courseName: q.courseName,
          oeCode: q.oeCode
        } : q
      )
    );
    setShowRefinementModal(false);
  };

  const handleRefine = async (question: any, prompt: string) => {
    return await questionsService.refineQuestion(question, prompt);
  };

  const handleDelete = (tempId: string) => {
    setQuestions(prev => prev.filter(q => q.tempId !== tempId));
  };

  const handleDeleteCaseGroup = (caseTitle: string) => {
    setQuestions(prev => prev.filter(q => !(q.caseTitle === caseTitle && (q.type === 'case_study' || q.type === 'case_based'))));
  };

  const handleSaveAll = () => {
    onSave(questions);
  };

  // Count display: total individual questions (case study sub-questions count individually)
  const totalQuestionCount = questions.length;

  /** Render alternatives for a question */
  const renderAlternatives = (question: any) => {
    if (!question.alternatives) return null;

    // Handle both object format {A: "text", B: "text"} and array format
    const alternativesArray = Array.isArray(question.alternatives)
      ? question.alternatives
      : Object.entries(question.alternatives).map(([letter, text]) => ({
          letter,
          text: typeof text === 'string' ? text : (text as any)?.text || String(text),
          correct: letter === question.correctAnswer,
          isCorrect: letter === question.correctAnswer
        }));

    if (alternativesArray.length === 0) return null;

    return (
      <div className="space-y-1.5 mt-2">
        {alternativesArray.map((alt: any) => {
          const isCorrect = alt.correct || alt.isCorrect;
          return (
            <div
              key={alt.letter}
              className={`p-2.5 rounded-lg text-sm flex items-start ${
                isCorrect
                  ? 'bg-green-50 border border-green-300'
                  : 'bg-gray-50 border border-gray-200'
              }`}
            >
              {isCorrect ? (
                <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0 mr-2 mt-0.5" />
              ) : (
                <XCircle className="w-4 h-4 text-gray-400 flex-shrink-0 mr-2 mt-0.5" />
              )}
              <span className="font-bold mr-1.5">{alt.letter})</span>
              <span className="flex-1">{formatAlternativeText(alt.text)}</span>
              {isCorrect && (
                <span className="ml-2 px-2 py-0.5 rounded text-xs font-semibold bg-green-600 text-white flex-shrink-0">
                  Correct
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 border-2 border-blue-200 rounded-lg p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Review Generated Questions
        </h2>
        <p className="text-gray-700">
          {totalQuestionCount} questions generated. Review and refine before saving to database.
        </p>
      </div>

      {/* Questions / Case Study Groups */}
      <div className="space-y-4">
        {previewItems.map((item, itemIndex) => {
          // ─── CASE STUDY GROUP ───
          if (item.kind === 'case_study') {
            const group = item;
            const groupKey = `case-${group.caseTitle}`;
            const isExpanded = expandedItems.has(groupKey);
            const firstSub = group.subQuestions[0];

            return (
              <div
                key={groupKey}
                className="bg-white border-2 border-blue-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Case Study Header */}
                <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-b-2 border-blue-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <span className="px-3 py-1 bg-blue-600 text-white rounded-full text-sm font-semibold">
                          Case Study
                        </span>
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          {group.subQuestions.length} Sub-Questions
                        </span>
                        {firstSub?.difficulty && (
                          <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                            {firstSub.difficulty}
                          </span>
                        )}
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                          {firstSub?.exam || '-'}
                        </span>
                        {firstSub?.sourceDocuments && firstSub.sourceDocuments.length > 0 && (
                          <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium">
                            {firstSub.sourceDocuments.length} {firstSub.sourceDocuments.length === 1 ? 'Source' : 'Sources'}
                          </span>
                        )}
                        {(() => {
                          const totalMin = group.subQuestions.reduce((sum: number, sq: any) => sum + (sq.estimatedTimeMin || sq.estimated_time_seconds ? Math.ceil((sq.estimated_time_seconds || 0) / 60) : 0), 0);
                          return totalMin > 0 ? (
                            <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium flex items-center gap-1">
                              <Clock className="w-3.5 h-3.5" />
                              {totalMin} min
                            </span>
                          ) : null;
                        })()}
                      </div>
                      <h3 className="text-lg font-bold text-blue-900 flex items-center">
                        <FileText className="w-5 h-5 mr-2 flex-shrink-0" />
                        {group.caseTitle}
                      </h3>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => toggleExpand(groupKey)}
                      className="flex items-center space-x-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg transition-colors border border-gray-200"
                    >
                      {isExpanded ? (
                        <><ChevronUp className="w-4 h-4" /><span>Collapse</span></>
                      ) : (
                        <><ChevronDown className="w-4 h-4" /><span>View Full Case</span></>
                      )}
                    </button>
                    <button
                      onClick={() => handleDeleteCaseGroup(group.caseTitle)}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete Case</span>
                    </button>
                  </div>
                </div>

                {/* Expanded: Case Context + Sub-Questions */}
                {isExpanded && (
                  <div className="p-6 space-y-5">
                    {/* ANBIMA Metadata */}
                    {group.metadados && (
                      <div className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs font-semibold text-gray-600 mb-2">Metadados (ANBIMA):</p>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-800">
                          {Object.entries(group.metadados).map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                              <span className="font-semibold">{String(value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Case Context */}
                    {group.caseContext && (
                      <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                        <strong className="text-sm text-blue-900 font-bold">Case Context:</strong>
                        <p className="text-sm text-blue-900 mt-2 leading-relaxed whitespace-pre-wrap">
                          {group.caseContext}
                        </p>
                      </div>
                    )}

                    {/* Case Data */}
                    {group.caseData && Object.keys(group.caseData).length > 0 && (
                      <div className="p-3 bg-white rounded-lg border border-blue-200">
                        <p className="text-xs font-semibold text-blue-800 mb-2">Dados do Caso:</p>
                        <div className="grid grid-cols-2 gap-2 text-xs text-blue-900">
                          {Object.entries(group.caseData).map(([key, value]) => (
                            <div key={key}>
                              <span className="font-medium">{key}:</span>{' '}
                              <span>{String(value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Sub-Questions */}
                    <div className="space-y-4">
                      {group.subQuestions.map((subQ: any, subIdx: number) => (
                        <div
                          key={subQ.tempId || subIdx}
                          className="p-4 border-2 border-gray-200 rounded-lg bg-white"
                        >
                          {/* Sub-question header */}
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold">
                                {subQ.subQuestionIndex || subQ.caseQuestionNumber || subIdx + 1}
                              </span>
                              <span className="text-sm font-semibold text-gray-900">
                                Sub-Question {subQ.subQuestionIndex || subQ.caseQuestionNumber || subIdx + 1}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              {subQ.oeCode && subQ.oeCode !== 'N/A' && (
                                <span className="px-2 py-0.5 text-[10px] font-semibold bg-green-100 text-green-700 rounded border border-green-200">
                                  {subQ.oeCode}
                                </span>
                              )}
                              {subQ.difficulty && (
                                <span className={`px-2 py-0.5 text-xs font-semibold rounded ${
                                  subQ.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                                  subQ.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                                  'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {subQ.difficulty}
                                </span>
                              )}
                              {(subQ.estimatedTimeMin || subQ.estimated_time_seconds) && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-amber-50 text-amber-700 rounded border border-amber-200 flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {subQ.estimatedTimeMin || Math.ceil((subQ.estimated_time_seconds || 0) / 60)} min
                                </span>
                              )}
                              <button
                                onClick={() => handleRefineClick(subQ)}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-md text-white rounded-lg transition-all text-xs"
                              >
                                <Sparkles className="w-3.5 h-3.5" />
                                <span>Refine</span>
                              </button>
                              <button
                                onClick={() => handleDelete(subQ.tempId)}
                                className="flex items-center space-x-1 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors text-xs"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Question text */}
                          <p className="text-sm text-gray-900 mb-3 leading-relaxed whitespace-pre-wrap">{subQ.stem}</p>

                          {/* Alternatives */}
                          {renderAlternatives(subQ)}

                          {/* Explanation */}
                          {subQ.explanation && (
                            <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                              <strong className="text-xs text-blue-900 font-bold">Explanation:</strong>
                              <p className="text-xs text-blue-900 mt-1 leading-relaxed whitespace-pre-wrap">{subQ.explanation}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          }

          // ─── INTERACTIVE QUESTION (ANBIMA Tipo 4) ───
          if (item.kind === 'interactive') {
            const question = item.question;
            const interactiveKey = `interactive-${question.tempId}`;
            const isExpanded = expandedItems.has(interactiveKey);
            const prompts = question.prompts || [];
            const optimalPath = question.optimalPath || [];

            return (
              <div
                key={interactiveKey}
                className="bg-white border-2 border-teal-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
              >
                {/* Interactive Header */}
                <div className="p-6 bg-gradient-to-r from-teal-50 to-cyan-50 border-b-2 border-teal-200">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <span className="px-3 py-1 bg-teal-600 text-white rounded-full text-sm font-semibold flex items-center gap-1">
                          <MessageCircle className="w-3.5 h-3.5" />
                          Interactive
                        </span>
                        <span className="px-3 py-1 bg-teal-100 text-teal-700 rounded-full text-sm font-medium">
                          {prompts.length} Dialogue Prompts
                        </span>
                        {question.difficulty && (
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            question.difficulty === 'hard' ? 'bg-red-100 text-red-700' :
                            question.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {question.difficulty}
                          </span>
                        )}
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                          {question.exam || '-'}
                        </span>
                        {question.oeCode && question.oeCode !== 'N/A' && (
                          <span className="px-2 py-0.5 text-xs font-semibold bg-teal-100 text-teal-700 rounded border border-teal-200">
                            {question.oeCode}
                          </span>
                        )}
                        {(question.estimatedTimeMin || question.estimated_time_seconds) && (
                          <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {question.estimatedTimeMin || Math.ceil((question.estimated_time_seconds || 0) / 60)} min
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-teal-900 line-clamp-2 mt-1">
                        {question.initialContext?.substring(0, 200) || question.stem?.substring(0, 200)}...
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-3">
                    <button
                      onClick={() => toggleExpand(interactiveKey)}
                      className="flex items-center space-x-2 px-4 py-2 bg-white hover:bg-gray-50 text-gray-700 rounded-lg transition-colors border border-gray-200"
                    >
                      {isExpanded ? (
                        <><ChevronUp className="w-4 h-4" /><span>Collapse</span></>
                      ) : (
                        <><ChevronDown className="w-4 h-4" /><span>View Dialogue</span></>
                      )}
                    </button>
                    <button
                      onClick={() => handleRefineClick(question)}
                      className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-lg text-white rounded-lg transition-all"
                    >
                      <Sparkles className="w-4 h-4" />
                      <span>Refine with AI</span>
                    </button>
                    <button
                      onClick={() => handleDelete(question.tempId)}
                      className="flex items-center space-x-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                  </div>
                </div>

                {/* Expanded: Initial Context + Dialogue Prompts */}
                {isExpanded && (
                  <div className="p-6 space-y-5">
                    {/* Behavioral Skills */}
                    {question.behavioralSkills && question.behavioralSkills.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        <span className="text-xs font-semibold text-gray-600">Skills Tested:</span>
                        {question.behavioralSkills.map((bs: any, idx: number) => (
                          <span key={idx} className="px-2 py-1 bg-teal-50 text-teal-700 rounded text-xs border border-teal-200">
                            {bs.skill?.replace(/_/g, ' ')} (L{bs.level})
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Initial Context */}
                    <div className="p-4 bg-teal-50 border-2 border-teal-200 rounded-lg">
                      <strong className="text-sm text-teal-900 font-bold flex items-center gap-1">
                        <MessageCircle className="w-4 h-4" />
                        Initial Context (Scenario)
                      </strong>
                      <p className="text-sm text-teal-900 mt-2 leading-relaxed whitespace-pre-wrap">
                        {question.initialContext || question.stem}
                      </p>
                    </div>

                    {/* Optimal Path */}
                    {optimalPath.length > 0 && (
                      <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                        <strong className="text-xs text-green-800 font-bold flex items-center gap-1">
                          <Star className="w-3.5 h-3.5" />
                          Optimal Path:
                        </strong>
                        <div className="flex items-center flex-wrap gap-1 mt-1">
                          {optimalPath.map((step: string, idx: number) => (
                            <span key={idx} className="flex items-center gap-1">
                              <span className="px-2 py-0.5 bg-green-600 text-white rounded text-xs font-semibold">
                                {step}
                              </span>
                              {idx < optimalPath.length - 1 && (
                                <ArrowRight className="w-3 h-3 text-green-500" />
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Dialogue Prompts */}
                    <div className="space-y-4">
                      <strong className="text-sm text-gray-700">Dialogue Flow:</strong>
                      {prompts.map((prompt: any, pIdx: number) => (
                        <div key={prompt.id || pIdx} className="border-2 border-gray-200 rounded-lg overflow-hidden">
                          {/* Prompt Header */}
                          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-teal-600 text-white text-xs font-bold">
                                {pIdx + 1}
                              </span>
                              <span className="text-xs font-semibold text-gray-500 uppercase">
                                {prompt.role === 'system' ? 'System' : 'Client'}
                              </span>
                              <span className="text-xs text-gray-400">({prompt.id})</span>
                            </div>
                            <p className="text-sm text-gray-900 mt-1 leading-relaxed whitespace-pre-wrap">
                              {prompt.text}
                            </p>
                          </div>

                          {/* Response Alternatives */}
                          {prompt.alternatives && prompt.alternatives.length > 0 && (
                            <div className="p-3 space-y-1.5">
                              {prompt.alternatives.map((alt: any) => {
                                const scoreColor =
                                  alt.score === 5 ? 'bg-green-100 border-green-300 text-green-800' :
                                  alt.score >= 3 ? 'bg-yellow-50 border-yellow-300 text-yellow-800' :
                                  alt.score >= 1 ? 'bg-orange-50 border-orange-300 text-orange-800' :
                                  'bg-red-50 border-red-300 text-red-800';
                                const scoreBadge =
                                  alt.score === 5 ? 'bg-green-600' :
                                  alt.score >= 3 ? 'bg-yellow-500' :
                                  alt.score >= 1 ? 'bg-orange-500' :
                                  'bg-red-500';

                                return (
                                  <div key={alt.letter} className={`p-2.5 rounded-lg text-sm border ${scoreColor}`}>
                                    <div className="flex items-start">
                                      <span className="font-bold mr-1.5 flex-shrink-0">{alt.letter})</span>
                                      <span className="flex-1">{formatAlternativeText(alt.text)}</span>
                                      <div className="flex items-center gap-2 ml-2 flex-shrink-0">
                                        <span className={`px-2 py-0.5 rounded text-xs font-bold text-white ${scoreBadge}`}>
                                          {alt.score}/5
                                        </span>
                                        {alt.nextPromptId && (
                                          <span className="px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded text-xs flex items-center gap-0.5">
                                            <ArrowRight className="w-3 h-3" />
                                            {alt.nextPromptId}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    {alt.explanation && (
                                      <p className="text-xs mt-1 ml-5 opacity-80 italic">{alt.explanation}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Explanation */}
                    {question.explanation && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <strong className="text-xs text-blue-900 font-bold">Overall Explanation:</strong>
                        <p className="text-xs text-blue-900 mt-1 leading-relaxed whitespace-pre-wrap">{question.explanation}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          }

          // ─── REGULAR QUESTION (MCQ / Short Answer / etc.) ───
          const question = item.question;
          const isExpanded = expandedItems.has(question.tempId);

          return (
            <div
              key={question.tempId || itemIndex}
              className="bg-white border-2 border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow"
            >
              {/* Question Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center flex-wrap gap-2 mb-2">
                    <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
                      Question {itemIndex + 1}
                    </span>
                    <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm">
                      {question.type}
                    </span>
                    <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm">
                      {question.difficulty}
                    </span>
                    <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                      {question.exam || '-'}
                    </span>
                    {question.courseName && (
                      <span className="px-3 py-1 bg-orange-100 text-orange-700 rounded-full text-sm font-medium">
                        {question.courseName}
                      </span>
                    )}
                    {question.sourceDocuments && question.sourceDocuments.length > 0 && (
                      <span className="px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-sm font-medium" title={question.sourceDocuments.map((d: any) => d.filename).join(', ')}>
                        {question.sourceDocuments.length} {question.sourceDocuments.length === 1 ? 'Source' : 'Sources'}
                      </span>
                    )}
                  </div>
                  {question.sourceDocuments && question.sourceDocuments.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {question.sourceDocuments.slice(0, 3).map((doc: any, idx: number) => (
                        <span
                          key={doc.documentId || idx}
                          className="inline-flex items-center px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded text-xs font-medium"
                          title={doc.title}
                        >
                          {doc.filename}
                        </span>
                      ))}
                      {question.sourceDocuments.length > 3 && (
                        <span className="inline-flex items-center px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">
                          +{question.sourceDocuments.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                  <p className="text-gray-900 text-sm line-clamp-2">
                    {question.stem?.substring(0, 200)}...
                  </p>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => toggleExpand(question.tempId)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
                >
                  {isExpanded ? (
                    <><EyeOff className="w-4 h-4" /><span>Hide</span></>
                  ) : (
                    <><Eye className="w-4 h-4" /><span>View Details</span></>
                  )}
                </button>
                <button
                  onClick={() => handleRefineClick(question)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:shadow-lg text-white rounded-lg transition-all"
                >
                  <Sparkles className="w-4 h-4" />
                  <span>Refine with AI</span>
                </button>
                <button
                  onClick={() => handleDelete(question.tempId)}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                  <span>Delete</span>
                </button>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                  {/* Metadata Section */}
                  <div className="grid grid-cols-2 gap-3 p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="text-xs font-semibold text-gray-600">Exam:</span>
                      <p className="text-sm text-gray-900 mt-0.5">{question.exam || '-'}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-600">Course:</span>
                      <p className="text-sm text-gray-900 mt-0.5">{question.courseName || 'Not specified'}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-600">Type:</span>
                      <p className="text-sm text-gray-900 mt-0.5">{question.type}</p>
                    </div>
                    <div>
                      <span className="text-xs font-semibold text-gray-600">Difficulty:</span>
                      <p className="text-sm text-gray-900 mt-0.5">{question.difficulty}</p>
                    </div>
                    {question.oeCode && question.oeCode !== 'N/A' && (
                      <div className="col-span-2">
                        <span className="text-xs font-semibold text-gray-600">OE Code:</span>
                        <p className="text-sm text-gray-900 mt-0.5">{question.oeCode}</p>
                      </div>
                    )}
                    {question.sourceDocuments && question.sourceDocuments.length > 0 && (
                      <div className="col-span-2">
                        <span className="text-xs font-semibold text-gray-600">Source Files:</span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {question.sourceDocuments.map((doc: any, idx: number) => (
                            <span
                              key={doc.documentId || idx}
                              className="inline-flex items-center px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700 rounded text-xs"
                              title={doc.title}
                            >
                              {doc.filename}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div>
                    <strong className="text-sm text-gray-700">Question:</strong>
                    <p className="text-sm text-gray-900 mt-1 whitespace-pre-wrap">{question.stem}</p>
                  </div>

                  {/* Alternatives */}
                  {question.alternatives && (
                    <div>
                      <strong className="text-sm text-gray-700">Alternatives:</strong>
                      {renderAlternatives(question)}
                    </div>
                  )}

                  {/* Explanation */}
                  <div className="p-3 bg-blue-50 border-2 border-blue-200 rounded-lg">
                    <strong className="text-sm text-blue-900 font-bold">Explanation:</strong>
                    <p className="text-sm text-gray-900 mt-2 leading-relaxed whitespace-pre-wrap">{question.explanation}</p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer Actions */}
      <div className="flex items-center justify-between p-6 bg-gray-50 border-2 border-gray-200 rounded-lg">
        <button
          onClick={onCancel}
          className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
        >
          Cancel & Discard
        </button>
        <button
          onClick={handleSaveAll}
          disabled={questions.length === 0}
          className="px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-lg font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center space-x-2"
        >
          <Save className="w-5 h-5" />
          <span>Save {totalQuestionCount} Questions to Question Bank</span>
        </button>
      </div>

      {/* Refinement Modal */}
      {showRefinementModal && selectedQuestion && (
        <QuestionRefinementModal
          isOpen={showRefinementModal}
          question={selectedQuestion}
          onClose={() => setShowRefinementModal(false)}
          onSave={handleRefineSave}
          onRefine={handleRefine}
        />
      )}
    </div>
  );
}
